import { Component, OnInit, Input, Output, EventEmitter, Inject, OnDestroy } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { Subscription, timer, Observable } from 'rxjs';

import {
  AuthenticationService,
  AuthenticationFactor,
  AuthenticationServiceStatus,
  ValidateUserResponse,
  UserNameStatus,
  FindMemberResponse,
  UserProfile,
  KnowledgeBase,
  AuthHelper,
  AuthProfile,
  UserAddress
} from '@caloptima/authentication';
import { CommonService } from '@caloptima/portal-foundation';
import { Constants } from '../../app.constants';
import { PortalConfig } from '../../portal-config';
import { ProviderRegistrationStatus } from '../../services/models/provider-registration-status';
import { RegistrationState } from '../../services/models/registration-state';
import { ProviderDetail } from '../../services/models/provider-detail';
import { BusyService } from '../../services/busy.service';
import { DOCUMENT } from '@angular/common';
import { AuthenticationFactorChoice } from '../../services/models/authentication-factor-choice.model';
import { UserService } from '../../services/user.service';
import { ErrorStatusModel } from '../../services/models/login.model';
import { Messages } from '../../app.messages';
import { VerifyIdentityModel } from '../../services/models/verify-identity.model';
import { ServiceUtility } from '../../services/service-utility';

@Component({
  selector: 'app-registration',
  styleUrls: ['./provider-registration.component.scss'],
  templateUrl: './provider-registration.component.html',
  providers: [CommonService]
})
export class ProviderRegistrationComponent implements OnInit, OnDestroy {
  public profile: UserProfile;
  public address: UserAddress;
  public stepNumber: number;
  public registrationState: RegistrationState;
  public componentUsing: string;
  private profileInvalid: boolean;
  private codeValid: boolean;
  public choices: AuthenticationFactorChoice[];
  public answers: string[];
  public choiceValue: string;

  public passcode: string;
  public answerQuestions: boolean;
  private selectedFactor: AuthenticationFactor;
  private requiresPasscode: boolean;
  public isBusy: boolean;
  public selectedFactorSubscription$: Subscription;
  public passcodeSubscription$: Subscription;
  private isValidPasscodeFormat: boolean;
  public errorSubscription$: Subscription;
  private errorStatus: ErrorStatusModel;
  private minutesRemaining: number;
  private timer: Observable<number>;
  public codeExpired: boolean;
  public expirationTimeRemaining: string;
  private timerSubscription: Subscription;
  public verifyIdentityModel: VerifyIdentityModel = new VerifyIdentityModel();

  constructor(
    private busy: BusyService,
    private authService: AuthenticationService,
    private authHelper: AuthHelper,
    private oauth: OAuthService,
    private router: Router,
    public stateService: CommonService,
    private constants: Constants,
    private configuration: PortalConfig,
    private userService: UserService,
    public messages: Messages,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.registrationState = new RegistrationState();
    this.stepNumber = 1;
    this.codeValid = false;
  }

  ngOnInit() {
    this.componentUsing = "provider-registration";

    this.verifyIdentityModel.TwoFactorOptionsDescription = 'To protect your account, we need to verify your identity. You will receive a temporary code using the delivery method you select below.';

    this.selectedFactorSubscription$ = this.userService.selectedFactorSubject$.subscribe(
      selectedFactor => {this.selectedFactor = selectedFactor; }
    );

    this.passcodeSubscription$ = this.userService.selectedFactorPasscodeSubject$.subscribe(
      passcode => {
        this.passcode = passcode;
      }
    );
   }

  onRegistrationStateUpdated($event: any) {
    this.registrationState = $event;
    this.getFactors();
    this.onBusy(false);
  }

  onBusy(busy: boolean) {
    this.busy.emit(busy);
  }

  public onEulaAccepted(version: number): void {
    this.registrationState.acceptedEulaVersion = version;
    if (version != null) {
      this.registrationState.status = ProviderRegistrationStatus.EulaAccepted;
    }
  }

  public onProviderSelected(provider: ProviderDetail): void {
    this.registrationState.provider = provider;
  }

  public isInvalid(): boolean {

    let invalid: boolean = false;
    if (this.stepNumber === 2 && !this.selectedFactor) {
      invalid = true;
    }
    else if (this.stepNumber === 3 && !this.isValidPasscodeFormat && !this.codeExpired) {
      invalid = true;
    }
    else if (this.stepNumber === 4 && (this.registrationState.acceptedEulaVersion == null || this.registrationState.acceptedEulaVersion == 0)) {
      invalid = true;
    }
    else if (this.stepNumber === 5 && this.registrationState.provider == null) {
      invalid = true;
    }
    return invalid;
  }

  validPasscode(validPasscode: boolean) {
    this.isValidPasscodeFormat = validPasscode;
  }

  onNext() {
    this.onBusy(false);
    if (this.codeValid && this.stepNumber === 1) {
      this.stepNumber++;
      this.stepNumber++;
    }
    this.stepNumber++;
  }

  onPrevious() {
    if (this.codeValid && this.stepNumber === 4) {
      this.stepNumber--;
      this.stepNumber--;
    }
    this.stepNumber--;
  }

  public register() {
    this.profileInvalid =
      (this.profile.firstName == null || this.profile.firstName == "" ||
        this.profile.lastName == null || this.profile.lastName == "" ||
        this.address.address1 == null || this.address.address1 == "" ||
        this.address.city == null || this.address.city == "" ||
        this.address.state == null || this.address.state == "" ||
        this.address.zip == null || this.address.zip == "" ||
        this.profile.email == null || this.profile.email == "" ||
        this.profile.mobilePhone == null || this.profile.mobilePhone == "");
    if (!this.profileInvalid) {
      const authProfile = new AuthProfile();
      authProfile.userProfile = this.profile;
      authProfile.clientId = this.configuration.ClientId;
      authProfile.address = this.address;

      this.authService.register(authProfile).subscribe({
        next: (result) => { },
        error: (err) => { }
      });
    }
  }

   public DoFactors() {
    this.isBusy = true;
    const responses: AuthenticationFactor[] = [];

    if (this.selectedFactor != null) {
      responses.push({ factorType: this.selectedFactor.factorType, factorID: this.selectedFactor.factorID, value: this.passcode });
    }

    const sub = this.authService.authenticateFactors(responses).subscribe((resp) => {
      if (resp.status === AuthenticationServiceStatus.NoError) {
        this.registrationState.codeValid = true;
        this.codeValid = true;
        this.onNext();
      } else if (resp.status === AuthenticationServiceStatus.OnetimePasscodeRequired) {
        if (this.stepNumber !== 3) {
          this.onNext();
        }
        this.userService.clearPasscode();
      } else if (resp.status === AuthenticationServiceStatus.NotallFactorsAuthenticated) {
        this.setErrorMessage(resp.status);
        this.userService.clearPasscode();
      }
      this.isBusy = false;
      this.startTimer();
      if (sub != null) {
        sub.unsubscribe();
      }
    }, (error) => {
      this.setErrorMessage(error.status, error.error);
      this.isBusy = false;
      this.userService.clearPasscode();
    });
  }

  public canShowPasscode(): boolean {
    return this.selectedFactor != null && this.requiresPasscode;
  }

  public hasFactors(): boolean {
    return this.selectedFactor != null;
  }

  public setErrorMessage(statusCode: number = null, statusMessage: string = null) {
    this.errorStatus = new ErrorStatusModel(statusCode, statusMessage);
    this.userService.setErrorMessage(this.errorStatus);
  }

  public getFactors() {
    this.clearErrorMessage();
    this.authService.getFactors().subscribe({
      next: (response)  => {
        this.choices = [];
        this.answers = [];

        this.isBusy = false;
        for (let i = 0; i < response.length; i++) {
          if (response[i].factorType !== 'Question') {

          this.choices.push(new AuthenticationFactorChoice(
            response[i].factorType,
            response[i].factorID,
            response[i].value,
            ServiceUtility.getFactorsChoiceDislaySequence(response[i].factorType)));
        }
      }
        this.choices.sort((a, b) => {
          return a.sequence - b.sequence;
        });
      },
      error: (err) => {
        this.isBusy = false;
      }
    });
  }

  public reSendCode() {
    this.getFactors();
    this.DoFactors();
  }

  public clearErrorMessage() {
    this.errorStatus = null;
    this.userService.clearErrorMessage();
  }

  startTimer() {
    if (this.timerSubscription != null) {
      this.timerSubscription.unsubscribe();
    }
    this.minutesRemaining = 15;
    this.expirationTimeRemaining = this.minutesRemaining + ':00' + this.messages.MinutesLabel;
    var secondsRemaining = this.minutesRemaining * 60;
    this.timerSubscription = timer(1000, 1000).subscribe(t => {
      if (--secondsRemaining > 0) {
        this.codeExpired = false;
        var label = secondsRemaining < 60 ? this.messages.SecondsLabel : this.messages.MinutesLabel;
        var secondsPart = (secondsRemaining % 60).toString();
        if (secondsPart.length == 1) {
          secondsPart = '0' + secondsPart;
        }
        this.expirationTimeRemaining = Math.floor(secondsRemaining / 60) + ':' + secondsPart + label;
      }
      else {
        this.codeExpired = true;
        this.timerSubscription.unsubscribe();
        this.setErrorMessage(13);
      }
    });
  }

  ngOnDestroy() {

    if (this.selectedFactorSubscription$) {
      this.userService.clearSelectedFactor();
      this.selectedFactorSubscription$.unsubscribe();
    }

    if (this.passcodeSubscription$) {
      this.passcodeSubscription$.unsubscribe();
    }

    if (this.timerSubscription != null) {
      this.timerSubscription.unsubscribe();
    }
  }
}
